import { CSSProperties } from "react";
import {
  DefaultPalette,
  FontSizes,
  FontWeights,
  IStyle,
  ITheme,
  mergeStyleSets,
} from "@fluentui/react";
import { CoherenceNeutralHeaderPalette } from "@coherence-design-system/styles";

// Theme-aware colors for the header global search. These are the same tokens
// the Coherence neutral header uses, so the search box + scope dropdown track
// the active theme instead of being hardcoded.
const HEADER_BG = CoherenceNeutralHeaderPalette.searchBoxBackground;
const HEADER_BG_HOVER = CoherenceNeutralHeaderPalette.searchBoxBackgroundHovered;
const HEADER_TEXT = CoherenceNeutralHeaderPalette.searchBoxText;
const HEADER_PLACEHOLDER = CoherenceNeutralHeaderPalette.searchBoxPlaceholder;

export const environmentLabelStyle: CSSProperties = {
  color: "#FFF59D",
  backgroundColor: "transparent",
  fontSize: FontSizes.size20,
  fontWeight: FontWeights.bold,
  marginLeft: "4px",
};

// EnhancedSearch search-box (the text input) styles.
export const headerSearchBoxStyles = {
  subComponentStyles: {
    searchBoxStyles: {
      root: {
        backgroundColor: HEADER_BG,
        selectors: {
          ":hover": { backgroundColor: HEADER_BG_HOVER },
          "&.is-active, &:focus-within": { backgroundColor: HEADER_BG_HOVER },
        },
      },
      field: {
        color: HEADER_TEXT,
        backgroundColor: "transparent",
        selectors: {
          "::placeholder": { color: HEADER_PLACEHOLDER },
          ":-ms-input-placeholder": { color: HEADER_PLACEHOLDER },
          "::-ms-input-placeholder": { color: HEADER_PLACEHOLDER },
        },
      },
      icon: { color: HEADER_TEXT },
    },
  },
};

// EnhancedSearch scope ComboBox (the "All / PORID / Title ..." selector) styles.
export const headerSearchScopeStyles = {
  root: { backgroundColor: HEADER_BG },
  subComponentStyles: {
    caretDownStyles: { root: { color: HEADER_TEXT } },
    comboBoxStyles: {
      root: {
        backgroundColor: HEADER_BG,
        color: HEADER_TEXT,
        selectors: {
          ":hover": { backgroundColor: HEADER_BG_HOVER },
          "&.is-open": { backgroundColor: HEADER_BG_HOVER },
          "&:after": { borderColor: "transparent" },
        },
      },
      rootHovered: {
        backgroundColor: HEADER_BG_HOVER,
        color: HEADER_TEXT,
        selectors: { "&:after": { borderColor: "transparent" } },
      },
      rootPressed: {
        backgroundColor: HEADER_BG_HOVER,
        color: HEADER_TEXT,
        selectors: {
          "&:after": { borderColor: "transparent" },
          ".ms-ComboBox-Input": { color: HEADER_TEXT },
        },
      },
      rootFocused: {
        backgroundColor: HEADER_BG,
        color: HEADER_TEXT,
        selectors: {
          "&:after": { borderColor: "transparent" },
          ".ms-ComboBox-Input": { color: HEADER_TEXT },
        },
      },
      input: {
        backgroundColor: "transparent",
        color: HEADER_TEXT,
        selectors: {
          ":hover": { backgroundColor: "transparent", color: HEADER_TEXT },
          "::placeholder": { color: HEADER_PLACEHOLDER },
        },
      },
      callout: {
        selectors: { ".ms-Callout-main": { backgroundColor: HEADER_BG } },
      },
      optionsContainer: { backgroundColor: HEADER_BG },
    },
  },
};

// Per-option styles for the scope dropdown list (portaled callout).
export const headerScopeOptionStyles = {
  root: { backgroundColor: HEADER_BG, color: HEADER_TEXT },
  optionText: { color: HEADER_TEXT },
  rootHovered: { backgroundColor: HEADER_BG_HOVER, color: HEADER_TEXT },
  rootFocused: { backgroundColor: HEADER_BG_HOVER, color: HEADER_TEXT },
  rootPressed: { backgroundColor: HEADER_BG_HOVER, color: HEADER_TEXT },
  rootChecked: { backgroundColor: HEADER_BG_HOVER, color: HEADER_TEXT },
  rootCheckedHovered: { backgroundColor: HEADER_BG_HOVER, color: HEADER_TEXT },
};

export const panelStyles = mergeStyleSets({ 
  panelLink: {
    margin: "8px 0",
    display: "block",
    fontSize: FontSizes.size14,
    fontWeight: FontWeights.semibold,
  } as IStyle,
  panelList: {
    listStyle: "none",
    paddingLeft: "0",
  } as IStyle,
});

export const settingsStyles = mergeStyleSets({
  root: {
    marginLeft: "0",
    marginRight: "0",
  } as IStyle,
});

export const profilePanelStyles = {
  root: {
    color: DefaultPalette.themeDark,
    fontSize: FontSizes.size14,
    fontWeight: FontWeights.semibold,
  },
  rootHovered: {
    color: DefaultPalette.themeDarker,
    fontSize: FontSizes.size14,
    fontWeight: FontWeights.semibold,
  },
  rootFocused: {
    color: DefaultPalette.themeDarker,
    fontSize: FontSizes.size14,
    fontWeight: FontWeights.semibold,
  },
  rootPressed: {
    color: DefaultPalette.themeDarker,
    fontSize: FontSizes.size14,
    fontWeight: FontWeights.semibold,
  },
  icon: {
    color: DefaultPalette.themeDark,
    fontSize: FontSizes.size14,
    fontWeight: FontWeights.semibold,
  },
  iconHovered: {
    color: DefaultPalette.themeDarker,
    fontSize: FontSizes.size14,
    fontWeight: FontWeights.semibold,
  },
  iconFocused: {
    color: DefaultPalette.themeDarker,
    fontSize: FontSizes.size14,
    fontWeight: FontWeights.semibold,
  },
  iconPressed: {
    color: DefaultPalette.themeDarker,
    fontSize: FontSizes.size14,
    fontWeight: FontWeights.semibold,
  },
};

// Layout/color classes for the header chrome (title bar, trigger row, panels).
export const appHeaderStyles = mergeStyleSets({
  headerRoot: {
    height: "48px",
  } as IStyle,
  headerTitle: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
  } as IStyle,
  headerLogo: {
    height: "24px",
    width: "24px",
  } as IStyle,
  environmentLabel: environmentLabelStyle,
  staticTriggers: {
    display: "flex",
    alignItems: "center",
  } as IStyle,
  settingsPanelSection: {
    padding: "16px 0",
  } as IStyle,
});

// ResizeGroup hosting the collapsible header triggers.
export const headerResizeGroupStyles = {
  root: { background: "transparent", height: "100%" },
};

// HeaderButton only accepts a `style` prop, so these are module-level constants
// rather than mergeStyleSets classes (still defined once, not per render).
export const notificationButtonStyle: CSSProperties = { position: "relative" };
export const headerButtonIconStyle: CSSProperties = { fontSize: 16 };

// "Theme" label inside the settings panel.
export const settingsLabelStyles = {
  root: { fontWeight: FontWeights.semibold, marginBottom: 8 },
};

// Unread-count badge overlaid on the notifications button. Theme-dependent, so
// it is built from the active theme rather than a static class.
export const getNotificationBadgeClass = (theme: ITheme): string =>
  mergeStyleSets({
    badge: {
      position: "absolute",
      top: 4,
      right: 4,
      backgroundColor: theme.palette.red,
      color: "#fff",
      borderRadius: "50%",
      width: 16,
      height: 16,
      fontSize: 10,
      fontWeight: 600,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      lineHeight: 1,
    } as IStyle,
  }).badge;

// Persona coin styles for the profile center. Theme-dependent.
export const getProfileCoinStyles = (theme: ITheme) => ({
  initials: {
    backgroundColor: theme.palette.themePrimary + " !important",
    color: theme.palette.white + " !important",
  },
});
